import { useState, useEffect } from 'react';
import logger from '@/utils/logger';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Package2, Sparkles, Users, TrendingUp, Play, Download, ArrowRight, FileText, Calendar, Menu, X, Home, LayoutDashboard, LogIn, Heart, FolderKanban } from 'lucide-react';
import { Button } from '@/components/ui/button';
import LanguageSelector from '@/components/LanguageSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Social media links
const SOCIAL_LINKS = {
  instagram: 'https://www.instagram.com/evenrounds/',
  facebook: 'https://www.facebook.com/profile.php?id=61592052717270',
  pinterest: 'https://fi.pinterest.com/evenrounds/'
};

export default function LandingPage() {
  const { t, i18n } = useTranslation();
  const [featuredDesigns, setFeaturedDesigns] = useState([]);
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [communityStats, setCommunityStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadFeaturedDesigns();
    loadFeaturedPosts();
    loadCommunityStats();
    checkAuth();
  }, [i18n.language]);

  const loadCommunityStats = async () => {
    try {
      const response = await axios.get(`${API}/community/stats`);
      setCommunityStats(response.data);
    } catch (error) {
      logger.error('Failed to load community stats:', error);
    }
  };

  // Normalize language code for API (handle variants like 'en-US@posix')
  const getApiLanguage = () => {
    const lang = i18n.resolvedLanguage || i18n.language || 'en';
    return lang.split('-')[0].split('@')[0];  // 'en-US@posix' -> 'en'
  };

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch {
      setUser(null);
    }
  };

  const loadFeaturedDesigns = async () => {
    try {
      // Don't filter by language for featured content - show all featured items
      const response = await axios.get(`${API}/designs/featured`);
      setFeaturedDesigns(response.data || []);
    } catch (error) {
      logger.error('Failed to load featured designs:', error);
    }
  };

  const loadFeaturedPosts = async () => {
    try {
      // Don't filter by language for featured content - show all featured items
      const response = await axios.get(`${API}/blog/featured`);
      setFeaturedPosts(response.data || []);
    } catch (error) {
      logger.error('Failed to load featured posts:', error);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="fixed top-0 w-full bg-white/80 backdrop-blur-md border-b border-stone-100 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <img 
                src="/logo-transparent.png" 
                alt="Even Rounds Logo" 
                className="h-10 w-10 object-contain"
              />
              <h1 className="text-2xl font-heading font-extrabold text-foreground tracking-tight">Even Rounds</h1>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-4">
              <LanguageSelector variant="ghost" />
              <Button
                variant="ghost"
                onClick={() => navigate('/designs')}
                className="rounded-full font-medium"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                {t('nav.designs')}
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/blog')}
                className="rounded-full font-medium"
              >
                <FileText className="h-4 w-4 mr-2" />
                {t('nav.blog')}
              </Button>
              {user ? (
                <Button 
                  data-testid="nav-mystash-button"
                  onClick={() => navigate('/dashboard')}
                  className="rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-0.5 transition-all duration-300 font-bold px-8 py-3"
                >
                  <LayoutDashboard className="h-4 w-4 mr-2" />
                  {t('nav.myStash')}
                </Button>
              ) : (
                <Button 
                  data-testid="nav-login-button"
                  onClick={handleLogin}
                  className="rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-0.5 transition-all duration-300 font-bold px-8 py-3"
                >
                  <LogIn className="h-4 w-4 mr-2" />
                  {t('nav.getStarted')}
                </Button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <div className="flex md:hidden items-center space-x-2">
              <LanguageSelector variant="ghost" />
              <button
                data-testid="landing-mobile-menu-button"
                className="p-2 rounded-lg hover:bg-stone-100 transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6 text-stone-600" />
                ) : (
                  <Menu className="h-6 w-6 text-stone-600" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-stone-100 bg-white">
            <div className="px-4 py-4 space-y-2">
              <button
                onClick={() => { navigate('/designs'); setMobileMenuOpen(false); }}
                className="w-full flex items-center space-x-3 text-stone-600 hover:bg-stone-50 hover:text-primary font-medium py-3 px-4 rounded-xl transition-colors"
              >
                <Sparkles className="h-5 w-5" />
                <span>{t('nav.designs')}</span>
              </button>
              <button
                onClick={() => { navigate('/blog'); setMobileMenuOpen(false); }}
                className="w-full flex items-center space-x-3 text-stone-600 hover:bg-stone-50 hover:text-primary font-medium py-3 px-4 rounded-xl transition-colors"
              >
                <FileText className="h-5 w-5" />
                <span>{t('nav.blog')}</span>
              </button>
              {user ? (
                <Button 
                  onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}
                  className="w-full rounded-full bg-primary text-primary-foreground shadow-lg font-bold mt-2"
                >
                  <LayoutDashboard className="h-4 w-4 mr-2" />
                  {t('nav.myStash')}
                </Button>
              ) : (
                <Button 
                  onClick={() => { handleLogin(); setMobileMenuOpen(false); }}
                  className="w-full rounded-full bg-primary text-primary-foreground shadow-lg font-bold mt-2"
                >
                  <LogIn className="h-4 w-4 mr-2" />
                  {t('nav.getStarted')}
                </Button>
              )}
            </div>
          </div>
        )}
      </nav>

      <section id="main-content" className="relative pt-32 pb-20 px-4 overflow-hidden" tabIndex="-1">
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url(https://images.unsplash.com/photo-1517697389675-cdbedd1c5583?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxjb3p5JTIwa25pdHRpbmclMjBjcmFmdGluZyUyMHRhYmxlJTIwb3ZlcmhlYWQlMjBmbGF0JTIwbGF5fGVufDB8fHx8MTc3MjM5NTQxNnww&ixlib=rb-4.1.0&q=85)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-5xl md:text-7xl font-heading font-extrabold tracking-tight mb-6 animate-fade-in-up">
              {t('landing.title')}
            </h2>
            <p className="text-lg md:text-xl text-stone-600 font-body mb-8 leading-relaxed">
              {t('landing.subtitle')}
            </p>
            <Button 
              data-testid="hero-cta-button"
              onClick={handleLogin}
              size="lg"
              className="rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-0.5 transition-all duration-300 font-bold px-12 py-6 text-lg"
            >
              {t('landing.getStarted')}
            </Button>
          </div>
        </div>
      </section>

      {/* Community Stats - Social Proof */}
      {communityStats && (communityStats.yarns > 0 || communityStats.projects > 0) && (
        <section className="py-8 px-4 bg-gradient-to-r from-primary/5 via-rose-50 to-primary/5 border-y border-primary/10">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-wrap justify-center items-center gap-6 md:gap-12">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Package2 className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-2xl md:text-3xl font-heading font-bold text-primary">
                    {communityStats.yarns.toLocaleString()}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {i18n.language === 'fi' ? 'lankaa varastossa' : 'yarns in stashes'}
                  </p>
                </div>
              </div>
              
              <div className="hidden md:block w-px h-12 bg-stone-200" />
              
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-secondary/10 flex items-center justify-center">
                  <FolderKanban className="h-6 w-6 text-secondary" />
                </div>
                <div>
                  <p className="text-2xl md:text-3xl font-heading font-bold text-secondary">
                    {communityStats.projects.toLocaleString()}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {i18n.language === 'fi' ? 'projektia' : 'projects'}
                  </p>
                </div>
              </div>
              
              <div className="hidden md:block w-px h-12 bg-stone-200" />
              
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-accent/10 flex items-center justify-center">
                  <Users className="h-6 w-6 text-accent" />
                </div>
                <div>
                  <p className="text-2xl md:text-3xl font-heading font-bold text-accent">
                    {communityStats.users.toLocaleString()}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {i18n.language === 'fi' ? 'käsityöläistä' : 'crafters'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-4xl md:text-5xl font-heading font-bold text-center mb-16 tracking-tight">
            {t('landing.features.title')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="rounded-3xl bg-gradient-to-br from-rose-50 to-orange-50 p-8 border border-stone-100 shadow-sm transition-all hover:shadow-xl hover:-translate-y-1">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Sparkles className="h-6 w-6 text-primary" />
              </div>
              <h4 className="text-2xl font-heading font-semibold mb-3">{t('landing.features.aiScanning.title')}</h4>
              <p className="text-stone-600 font-body leading-relaxed">
                {t('landing.features.aiScanning.description')}
              </p>
            </div>

            <div className="rounded-3xl bg-gradient-to-br from-yellow-50 to-amber-50 p-8 border border-stone-100 shadow-sm transition-all hover:shadow-xl hover:-translate-y-1">
              <div className="h-12 w-12 rounded-full bg-secondary/10 flex items-center justify-center mb-4">
                <Package2 className="h-6 w-6 text-secondary" />
              </div>
              <h4 className="text-2xl font-heading font-semibold mb-3">{t('landing.features.smartInventory.title')}</h4>
              <p className="text-stone-600 font-body leading-relaxed">
                {t('landing.features.smartInventory.description')}
              </p>
            </div>

            <div className="rounded-3xl bg-gradient-to-br from-emerald-50 to-teal-50 p-8 border border-stone-100 shadow-sm transition-all hover:shadow-xl hover:-translate-y-1">
              <div className="h-12 w-12 rounded-full bg-accent/10 flex items-center justify-center mb-4">
                <TrendingUp className="h-6 w-6 text-accent" />
              </div>
              <h4 className="text-2xl font-heading font-semibold mb-3">{t('landing.features.projectTracking.title')}</h4>
              <p className="text-stone-600 font-body leading-relaxed">
                {t('landing.features.projectTracking.description')}
              </p>
            </div>

            <div className="rounded-3xl bg-gradient-to-br from-blue-50 to-indigo-50 p-8 border border-stone-100 shadow-sm transition-all hover:shadow-xl hover:-translate-y-1">
              <div className="h-12 w-12 rounded-full bg-blue-500/10 flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-blue-500" />
              </div>
              <h4 className="text-2xl font-heading font-semibold mb-3">{t('landing.features.comingSoon.title')}</h4>
              <p className="text-stone-600 font-body leading-relaxed">
                {t('landing.features.comingSoon.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Crochet Designs Section */}
      {featuredDesigns.length > 0 && (
        <section className="py-20 px-4 bg-gradient-to-br from-rose-50 to-orange-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h3 className="text-4xl md:text-5xl font-heading font-bold tracking-tight mb-4">
                {t('landing.designsSection.title', 'Crochet')} <span className="text-primary">{t('nav.designs', 'Designs')}</span>
              </h3>
              <p className="text-lg text-stone-600 max-w-2xl mx-auto">
                {t('landing.designsSection.subtitle', 'Explore our curated collection of crochet patterns from beginner to advanced.')}
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {featuredDesigns.slice(0, 3).map((design) => (
                <div
                  key={design.design_id}
                  onClick={() => navigate(`/designs/${design.slug || design.design_id}`)}
                  className="group rounded-3xl bg-white overflow-hidden shadow-sm hover:shadow-xl transition-all cursor-pointer hover:-translate-y-1"
                >
                  <div className="aspect-[4/3] bg-gradient-to-br from-rose-100 to-orange-100 relative overflow-hidden">
                    {design.images && design.images.length > 0 ? (
                      <img
                        src={design.images[0].url}
                        alt={design.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Sparkles className="h-16 w-16 text-primary/50" />
                      </div>
                    )}
                    {design.youtube_video_id && (
                      <div className="absolute top-3 right-3 bg-black/70 text-white px-2 py-1 rounded-full text-xs flex items-center">
                        <Play className="h-3 w-3 mr-1" />
                        {t('designs.video')}
                      </div>
                    )}
                    {design.is_free ? (
                      <div className="absolute top-3 left-3 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                        {t('designs.free')}
                      </div>
                    ) : (
                      <div className="absolute top-3 left-3 bg-primary text-white px-3 py-1 rounded-full text-sm font-bold">
                        €{design.price}
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h4 className="text-xl font-heading font-semibold mb-2 group-hover:text-primary transition-colors">
                      {design.title}
                    </h4>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {design.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center">
              <Button
                onClick={() => navigate('/designs')}
                size="lg"
                className="rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-0.5 transition-all duration-300 font-bold px-8"
              >
                {t('landing.designsSection.viewAll', 'View All Designs')}
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Featured Blog Posts Section */}
      {featuredPosts.length > 0 && (
        <section className="py-20 px-4 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h3 className="text-4xl md:text-5xl font-heading font-bold tracking-tight mb-4">
                {t('landing.blog.sectionTitle', 'From the')} <span className="text-primary">{t('landing.blog.sectionHighlight', 'Blog')}</span>
              </h3>
              <p className="text-lg text-stone-600 max-w-2xl mx-auto">
                {t('blog.subtitle')}
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {featuredPosts.slice(0, 3).map((post) => (
                <article
                  key={post.post_id}
                  onClick={() => navigate(`/blog/${post.slug || post.post_id}`)}
                  className="group rounded-3xl bg-gradient-to-br from-stone-50 to-stone-100 overflow-hidden shadow-sm hover:shadow-xl transition-all cursor-pointer hover:-translate-y-1 border border-stone-100"
                >
                  {post.featured_image ? (
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={post.featured_image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  ) : (
                    <div className="aspect-video bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center">
                      <FileText className="h-16 w-16 text-primary/30" />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-3 text-sm text-muted-foreground mb-3">
                      <span className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        {formatDate(post.created_at)}
                      </span>
                      <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium capitalize">
                        {post.category}
                      </span>
                    </div>
                    <h4 className="text-xl font-heading font-semibold mb-2 group-hover:text-primary transition-colors line-clamp-2">
                      {post.title}
                    </h4>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {post.excerpt}
                    </p>
                  </div>
                </article>
              ))}
            </div>

            <div className="text-center">
              <Button
                onClick={() => navigate('/blog')}
                size="lg"
                variant="outline"
                className="rounded-full shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 font-bold px-8"
              >
                {i18n.language === 'fi' ? 'Lue Kaikki Artikkelit' : 'Read All Posts'}
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Support Even Rounds Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-rose-50/50 to-amber-50/50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-rose-100 mb-6">
            <Heart className="h-8 w-8 text-rose-500" />
          </div>
          <h3 className="text-3xl md:text-4xl font-heading font-bold tracking-tight mb-4">
            {t('landing.support.title', 'Support Even Rounds')}
          </h3>
          <p className="text-lg text-stone-600 font-body mb-8 max-w-2xl mx-auto">
            {t('landing.support.subtitle', 'Even Rounds is a passion project built by yarn enthusiasts, for yarn enthusiasts. Your support helps us continue development and keep the service running.')}
          </p>
          
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-6">
            <Button
              onClick={() => window.open('https://buy.stripe.com/test_00g5kU8Ss8I6eWc000', '_blank')}
              size="lg"
              className="rounded-full bg-rose-500 hover:bg-rose-600 text-white shadow-lg shadow-rose-500/30 hover:shadow-rose-500/50 font-bold px-8"
            >
              <Heart className="h-5 w-5 mr-2" />
              {t('landing.support.monthly', 'Monthly Support')}
            </Button>
            <Button
              onClick={() => window.open('https://buy.stripe.com/test_14k5kUfgQ8I66pGcMN', '_blank')}
              size="lg"
              variant="outline"
              className="rounded-full border-rose-300 text-rose-600 hover:bg-rose-50 font-bold px-8"
            >
              {t('landing.support.oneTime', 'One-time Donation')}
            </Button>
          </div>
          
          <p className="text-sm text-stone-500">
            {t('landing.support.stripeNotice', 'Payment processed securely via Stripe')}
          </p>
        </div>
      </section>

      <section className="py-20 px-4 relative overflow-hidden">
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <h3 className="text-4xl md:text-5xl font-heading font-bold mb-6 tracking-tight">
            {t('landing.cta.title')}
          </h3>
          <p className="text-lg text-stone-600 font-body mb-8 max-w-2xl mx-auto">
            {t('landing.cta.subtitle')}
          </p>
          <Button 
            data-testid="footer-cta-button"
            onClick={handleLogin}
            size="lg"
            className="rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-0.5 transition-all duration-300 font-bold px-12 py-6 text-lg"
          >
            {t('landing.cta.button')}
          </Button>
        </div>
      </section>

      {/* Instagram Feed Section */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-stone-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h3 className="text-3xl md:text-4xl font-heading font-bold tracking-tight mb-3">
              {i18n.language === 'fi' ? 'Seuraa meitä' : 'Follow Us'} <span className="text-primary">@evenrounds</span>
            </h3>
            <p className="text-muted-foreground">
              {i18n.language === 'fi' 
                ? 'Inspiraatiota ja vinkkejä Instagramissa' 
                : 'Inspiration and tips on Instagram'}
            </p>
          </div>
          
          {/* Instagram Embed - using official embed */}
          <div className="flex justify-center">
            <a 
              href={SOCIAL_LINKS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col items-center gap-4 p-8 rounded-3xl bg-white border border-stone-100 shadow-sm hover:shadow-xl transition-all hover:-translate-y-1"
            >
              <div className="w-24 h-24 rounded-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 p-1">
                <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                  <svg className="h-12 w-12 text-pink-600" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </div>
              </div>
              <div className="text-center">
                <p className="font-heading font-bold text-xl">@evenrounds</p>
                <p className="text-sm text-muted-foreground">
                  {i18n.language === 'fi' ? 'Seuraa Instagramissa' : 'Follow on Instagram'}
                </p>
              </div>
              <span className="px-6 py-2 rounded-full bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600 text-white font-medium group-hover:scale-105 transition-transform">
                {i18n.language === 'fi' ? 'Seuraa' : 'Follow'}
              </span>
            </a>
          </div>
        </div>
      </section>

      <footer className="py-8 px-4 border-t border-stone-100 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <img 
              src="/logo-transparent.png" 
              alt="Even Rounds Logo" 
              className="h-8 w-8 object-contain"
            />
            <span className="text-xl font-heading font-bold">Even Rounds</span>
          </div>
          
          {/* Social Media Links */}
          <div className="flex justify-center gap-4 mb-6">
            <a 
              href={SOCIAL_LINKS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 flex items-center justify-center text-white hover:scale-110 transition-transform"
              aria-label="Instagram"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
            <a 
              href={SOCIAL_LINKS.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full bg-[#1877F2] flex items-center justify-center text-white hover:scale-110 transition-transform"
              aria-label="Facebook"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
            <a 
              href={SOCIAL_LINKS.pinterest}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full bg-[#E60023] flex items-center justify-center text-white hover:scale-110 transition-transform"
              aria-label="Pinterest"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/>
              </svg>
            </a>
          </div>
          
          {/* Login Link - always visible */}
          <div className="flex justify-center mb-6">
            {user ? (
              <Link
                to="/dashboard"
                data-testid="dashboard-footer-btn"
                className="text-sm text-primary font-medium hover:text-primary/80 transition-colors flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-primary/30 hover:border-primary hover:bg-primary/5"
              >
                <LogIn className="h-4 w-4" />
                {t('landing.footer.dashboard', 'My Dashboard')}
              </Link>
            ) : (
              <Link
                to="/login"
                data-testid="login-footer-btn"
                className="text-sm text-primary font-medium hover:text-primary/80 transition-colors flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-primary/30 hover:border-primary hover:bg-primary/5"
              >
                <LogIn className="h-4 w-4" />
                {t('landing.footer.login', 'Sign In')}
              </Link>
            )}
          </div>
          
          {/* Legal Links */}
          <div className="flex flex-wrap justify-center gap-4 mb-4 text-sm">
            <Link to="/privacy" className="text-stone-500 hover:text-primary transition-colors">
              {t('landing.footer.privacy') || 'Privacy Policy'}
            </Link>
            <Link to="/terms" className="text-stone-500 hover:text-primary transition-colors">
              {t('landing.footer.terms') || 'Terms of Service'}
            </Link>
            <Link to="/cookies" className="text-stone-500 hover:text-primary transition-colors">
              {t('landing.footer.cookies') || 'Cookies'}
            </Link>
            <Link to="/imprint" className="text-stone-500 hover:text-primary transition-colors">
              {t('landing.footer.imprint') || 'Imprint'}
            </Link>
            <Link to="/refund" className="text-stone-500 hover:text-primary transition-colors">
              {t('landing.footer.refund') || 'Refund Policy'}
            </Link>
          </div>
          
          <p className="text-sm text-stone-500 font-body text-center">
            © 2026 Vera Value Oy. {t('landing.footer.copyright')}
          </p>
        </div>
      </footer>
    </div>
  );
}
